import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import BreakdownDialogTitle from "../../../routes/pricing-reports/BreakdownDialogTitle";
import GalleryObservationThumb from "../../../routes/pricing-reports/GalleryObservationThumb";
import ProductImageViewer from "../../../routes/pricing-reports/ProductImageViewer";
import { withDirectusAssetTransformKey } from "../../../routes/pricing-reports/pricingReportImageUrls";
import LocationLabelDetailMetadata from "./LocationLabelDetailMetadata";

function buildGalleryThumbs(item) {
  return (item?.images || []).map((url, imageIndex) => ({
    key: `${item.id}:${imageIndex}`,
    url,
    thumbnailUrl: withDirectusAssetTransformKey(url),
    label: `Image ${imageIndex + 1}`,
    imageIndex,
    isPlaceholder: false,
  }));
}

function formatDialogTitle(item) {
  const productName = String(item?.productName || "").trim();
  const hasProduct = productName && productName !== "—";
  const grade = String(item?.grade || "").trim();
  const country = String(item?.countryLabel || "").trim();
  if (hasProduct && country) {
    return `${productName} · ${country}`;
  }
  if (hasProduct) {
    return productName;
  }
  if (country && grade) {
    return `${country} · ${grade}`;
  }
  return country || grade || "Label";
}

function LocationLabelDetailDialog({ open, item, onClose }) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedThumbKey, setSelectedThumbKey] = useState("");

  const galleryThumbs = useMemo(() => (item ? buildGalleryThumbs(item) : []), [item]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedThumbKey(galleryThumbs[0]?.key ?? "");
  }, [open, item?.id, galleryThumbs]);

  const selectedThumb = useMemo(
    () => galleryThumbs.find((thumb) => thumb.key === selectedThumbKey) ?? null,
    [galleryThumbs, selectedThumbKey]
  );

  const viewerImageUrl = selectedThumb?.url ?? "";
  const viewerAlt = item
    ? `${item.productName !== "—" ? item.productName : item.countryLabel} label image ${(selectedThumb?.imageIndex ?? 0) + 1}`
    : "Label image";

  return (
    <Dialog
      className="insights-dialog"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobileViewport}
    >
      <BreakdownDialogTitle title={formatDialogTitle(item)} onClose={onClose} />
      <DialogContent dividers>
        {!item ? null : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              Label analysis ID: {item.id || "Not available"}
            </Typography>
            {viewerImageUrl ? (
              <ProductImageViewer src={viewerImageUrl} alt={viewerAlt} minHeight={400} maxHeight={520} />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 240,
                  borderRadius: 1,
                  border: "1px solid #333",
                  backgroundColor: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="grey.400">
                  No image for this record
                </Typography>
              </Box>
            )}

            {galleryThumbs.length > 1 ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  gap: 1,
                  pb: 1,
                  scrollSnapType: "x mandatory",
                }}
                role="list"
                aria-label="Label images"
              >
                {galleryThumbs.map((thumb) => (
                  <GalleryObservationThumb
                    key={thumb.key}
                    thumb={thumb}
                    isSelected={selectedThumbKey === thumb.key}
                    onSelect={setSelectedThumbKey}
                  />
                ))}
              </Stack>
            ) : null}

            <LocationLabelDetailMetadata item={item} />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default LocationLabelDetailDialog;
